import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui'
import { Select } from '../../components/ui/Select'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { useAuth } from '../../components/AuthProvider'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import { Plus, Link as LinkIcon, Grid, List, Copy, Check, Trash2, Edit, ExternalLink, ChevronDown, ChevronUp, Eye, X } from 'lucide-react'
import AddSupplierModal from '../../components/tpsm/AddSupplierModal'
import EditSupplierModal from '../../components/tpsm/EditSupplierModal'
import SuppliersCriticalityDistributionChart from '../../components/tpsm/SuppliersCriticalityDistributionChart'
import { pageScope } from '../../lib/cssScope'
import { USE_MOCK_TPSM_INVENTORY_DATA } from '../../mocks/useMockTpsmInventory'
import tpsmSuppliersFixture from '../../mocks/fixtures/tpsm-suppliers.json'
import tpsmCriticalityFixture from '../../mocks/fixtures/tpsm-criticality.json'
import tpsmAssessmentsSummaryFixture from '../../mocks/fixtures/tpsm-assessments-summary.json'
import { getMockAssessmentsBySupplierId } from '../../mocks/mockTpsmAssessmentsBySupplier'

type Supplier = {
  id: string
  name: string
  contactName?: string
  email?: string
  website?: string
  categoryAccess: string
  supplierType: string
  subGradation?: string
  standards: string[]
  compliance: number
  lastAssessment?: string
  criticality?: string
  status?: string
  portalToken?: {
    token: string
    portalUrl: string
    expiresAt: string
    createdAt: string
  }
}

const SupplierInventory: React.FC = () => {
  const { token } = useAuth()
  const { t } = useI18n()
  const navigate = useNavigate()
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table')
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState({
    category: 'all',
    criticality: 'all',
    status: 'all',
    standards: 'all'
  })
  const [copiedLink, setCopiedLink] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [expandedSupplier, setExpandedSupplier] = useState<string | null>(null)
  const [criticalityData, setCriticalityData] = useState<any[]>([])
  const [supplierAssessments, setSupplierAssessments] = useState<Record<string, any[]>>({})
  const [assessmentsSummary, setAssessmentsSummary] = useState<Record<string, unknown> | null>(null)

  useEffect(() => {
    fetchSuppliers()
    fetchAssessmentsSummary()
    fetchCriticalityData()
  }, [token])

  // Load assessments for all suppliers after suppliers are loaded
  useEffect(() => {
    if (suppliers.length > 0 && Object.keys(supplierAssessments).length === 0) {
      // Load assessments for all suppliers in parallel (only once)
      const loadAllAssessments = async () => {
        const assessmentPromises = suppliers.map(s => fetchSupplierAssessments(s.id))
        await Promise.all(assessmentPromises)
      }
      loadAllAssessments()
    }
  }, [suppliers.length]) // Only trigger when suppliers list changes

  const fetchCriticalityData = async () => {
    if (USE_MOCK_TPSM_INVENTORY_DATA) {
      const data = tpsmCriticalityFixture as { suppliers?: unknown[] }
      if (Array.isArray(data.suppliers)) {
        setCriticalityData(data.suppliers as any[])
      }
      return
    }
    try {
      const res = await fetch(API_ENDPOINTS.TPSM_CRITICALITY, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      if (res.ok && Array.isArray(data.suppliers)) {
        setCriticalityData(data.suppliers)
      }
    } catch (e: any) {
      console.error('Failed to fetch criticality data:', e)
    }
  }

  const fetchSuppliers = async () => {
    setLoading(true)
    try {
      if (USE_MOCK_TPSM_INVENTORY_DATA) {
        const dto = tpsmSuppliersFixture as { suppliers?: Supplier[] }
        setSuppliers(Array.isArray(dto.suppliers) ? dto.suppliers : [])
        setError('')
        return
      }
      const res = await fetch(API_ENDPOINTS.SUPPLIERS, { 
        headers: { Authorization: `Bearer ${token}` } 
      })
      const dto = await safeJson(res)
      setSuppliers(Array.isArray(dto.suppliers) ? dto.suppliers : [])
    } catch (e: any) {
      setError(e.message || 'Failed to load suppliers')
    } finally {
      setLoading(false)
    }
  }

  const fetchSupplierAssessments = async (supplierId: string) => {
    if (USE_MOCK_TPSM_INVENTORY_DATA) {
      const rows = getMockAssessmentsBySupplierId(supplierId) as unknown[]
      setSupplierAssessments((prev) => ({
        ...prev,
        [supplierId]: rows,
      }))
      return
    }
    try {
      const res = await fetch(`${API_ENDPOINTS.TPSM_ASSESSMENTS}?supplierId=${supplierId}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      if (res.ok && Array.isArray(data)) {
        setSupplierAssessments(prev => ({
          ...prev,
          [supplierId]: data
        }))
      }
    } catch (e: any) {
      console.error('Failed to fetch supplier assessments:', e)
    }
  }

  const handleExpandSupplier = (supplierId: string) => {
    if (expandedSupplier === supplierId) {
      setExpandedSupplier(null)
    } else {
      setExpandedSupplier(supplierId)
      if (!supplierAssessments[supplierId]) {
        fetchSupplierAssessments(supplierId)
      }
    }
  }

  const fetchAssessmentsSummary = async () => {
    if (USE_MOCK_TPSM_INVENTORY_DATA) {
      setAssessmentsSummary(tpsmAssessmentsSummaryFixture as Record<string, unknown>)
      return
    }
    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)
      
      const res = await fetch(API_ENDPOINTS.TPSM_ASSESSMENTS_SUMMARY, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      })
      clearTimeout(timeoutId)
      if (res.ok) {
        const data = await safeJson(res)
        if (data && typeof data === 'object') {
          setAssessmentsSummary(data as Record<string, unknown>)
        }
      }
    } catch (e: any) {
      // Silently fail - assessments might not be available yet or timed out
      if (e.name !== 'AbortError') {
        console.warn('[Inventory] Failed to fetch assessments summary:', e)
      }
    }
  }

  const generatePortalLink = async (supplierId: string) => {
    try {
      const res = await fetch(API_ENDPOINTS.SUPPLIER_PORTAL_TOKEN(supplierId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await safeJson(res)
      if (data.portalUrl) {
        // Refresh suppliers to get updated portal URL
        await fetchSuppliers()
        await navigator.clipboard.writeText(data.portalUrl)
        setCopiedLink(supplierId)
        setTimeout(() => setCopiedLink(null), 3000)
      }
    } catch (e: any) {
      alert(t('tpsm.inventory.portalLinkError', 'Failed to generate portal link: {error}', { error: e.message }))
    }
  }

  const deleteSupplier = async (supplierId: string, supplierName: string) => {
    if (!confirm(t('tpsm.inventory.confirmDelete', 'Are you sure you want to delete supplier "{name}"?', { name: supplierName }))) {
      return
    }
    
    try {
      const res = await fetch(`${API_ENDPOINTS.SUPPLIERS}/${supplierId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (res.ok) {
        fetchSuppliers()
        alert(t('tpsm.inventory.supplierDeleted', 'Supplier deleted successfully'))
      } else {
        const data = await safeJson(res)
        alert(t('tpsm.inventory.deleteError', 'Failed to delete supplier: {error}', { error: data.message || 'Unknown error' }))
      }
    } catch (e: any) {
      alert(t('tpsm.inventory.deleteError', 'Failed to delete supplier: {error}', { error: e.message }))
    }
  }

  // Extract unique values for filters
  const categories = useMemo(() => {
    const cats = new Set(suppliers.map(s => s.categoryAccess).filter(Boolean))
    // Add default categories if none exist
    if (cats.size === 0) {
      return ['IT Services', 'Cloud Provider', 'Software Vendor', 'Hardware Supplier', 'Consulting']
    }
    return Array.from(cats).sort()
  }, [suppliers])

  const criticalities = useMemo(() => {
    const crits = new Set(suppliers.map(s => s.criticality).filter(Boolean))
    // Add default criticalities if none exist
    if (crits.size === 0) {
      return ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW']
    }
    return Array.from(crits).sort()
  }, [suppliers])

  const statuses = useMemo(() => {
    const sts = new Set(suppliers.map(s => s.status).filter(Boolean))
    // Add default statuses if none exist
    if (sts.size === 0) {
      return ['ACTIVE', 'INACTIVE', 'PENDING', 'SUSPENDED']
    }
    return Array.from(sts).sort()
  }, [suppliers])

  const allStandards = useMemo(() => {
    const stds = new Set<string>()
    suppliers.forEach(s => {
      if (Array.isArray(s.standards)) {
        s.standards.forEach(st => stds.add(st))
      }
    })
    // Add default standards if none exist
    if (stds.size === 0) {
      return ['GDPR', 'ISO27001', 'SOC2', 'NIST', 'SDLC_SECURITY', 'TECH_CONTROLS']
    }
    return Array.from(stds).sort()
  }, [suppliers])

  const categorySelectOptions = useMemo(
    () => [
      { value: 'all', label: t('tpsm.inventory.filters.allCategories', 'All Categories') },
      ...categories.map((c) => ({ value: c, label: c })),
    ],
    [categories, t],
  )
  const criticalitySelectOptions = useMemo(
    () => [
      { value: 'all', label: t('tpsm.inventory.filters.allCriticalities', 'All Criticalities') },
      ...criticalities.map((c) => ({ value: c, label: c })),
    ],
    [criticalities, t],
  )
  const statusSelectOptions = useMemo(
    () => [
      { value: 'all', label: t('tpsm.inventory.filters.allStatuses', 'All Statuses') },
      ...statuses.map((s) => ({ value: s, label: s })),
    ],
    [statuses, t],
  )
  const standardsSelectOptions = useMemo(
    () => [
      { value: 'all', label: t('tpsm.inventory.filters.allStandards', 'All Standards') },
      ...allStandards.map((s) => ({ value: s, label: s })),
    ],
    [allStandards, t],
  )

  // Filter and search suppliers
  const filteredSuppliers = useMemo(() => {
    let filtered = suppliers

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(query) ||
        s.email?.toLowerCase().includes(query) ||
        s.contactName?.toLowerCase().includes(query)
      )
    }

    // Category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(s => s.categoryAccess === filters.category)
    }

    // Criticality filter
    if (filters.criticality !== 'all') {
      filtered = filtered.filter(s => s.criticality === filters.criticality)
    }

    // Status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(s => s.status === filters.status)
    }

    // Standards filter
    if (filters.standards !== 'all') {
      filtered = filtered.filter(s => 
        Array.isArray(s.standards) && s.standards.includes(filters.standards)
      )
    }

    return filtered
  }, [suppliers, searchQuery, filters])

  // Calculate statistics
  const stats = useMemo(() => {
    const total = suppliers.length
    let avgCompliance =
      total > 0
        ? Math.round(suppliers.reduce((sum, s) => sum + (s.compliance || 0), 0) / total)
        : 0
    const byStatus = assessmentsSummary?.byStatus as { IN_PROGRESS?: number } | undefined
    if (typeof assessmentsSummary?.averageComplianceScore === 'number') {
      avgCompliance = Math.round(assessmentsSummary.averageComplianceScore as number)
    }
    const highRisk = suppliers.filter((s) => (s.compliance || 0) < 40).length
    let activeAssessments = suppliers.filter(
      (s) => s.lastAssessment && new Date(s.lastAssessment) > new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
    ).length
    if (typeof byStatus?.IN_PROGRESS === 'number') {
      activeAssessments = byStatus.IN_PROGRESS
    }

    return { total, avgCompliance, highRisk, activeAssessments }
  }, [suppliers, assessmentsSummary])

  const getCompliancePillClass = (val: number) => {
    if (val > 85) return listStyles.complianceHigh
    if (val >= 40) return listStyles.complianceMedium
    return listStyles.complianceLow
  }

  const getCriticalityBadgeClass = (c?: string) => {
    if (!c) return listStyles.badgeDefault
    const u = c.toUpperCase()
    if (u === 'CRITICAL' || u === 'HIGH') return listStyles.badgeMissed
    if (u === 'MEDIUM') return listStyles.badgePlanned
    if (u === 'LOW') return listStyles.badgeCompleted
    return listStyles.badgeDefault
  }

  const hasActiveFilters = useMemo(
    () =>
      searchQuery.trim() !== '' ||
      filters.category !== 'all' ||
      filters.criticality !== 'all' ||
      filters.status !== 'all' ||
      filters.standards !== 'all',
    [searchQuery, filters],
  )

  const clearFilters = () => {
    setSearchQuery('')
    setFilters({ category: 'all', criticality: 'all', status: 'all', standards: 'all' })
  }

  const supplierInventoryThead = (
    <thead>
      <tr>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.supplierName', 'Supplier Name')}
        </th>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.category', 'Category')}
        </th>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.criticality', 'Criticality')}
        </th>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.assignedStandards', 'Assigned Standards')}
        </th>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.compliancePercent', 'Compliance %')}
        </th>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.lastAssessment', 'Last Assessment')}
        </th>
        <th className={listStyles.th} scope="col">
          {t('tpsm.inventory.table.actions', 'Actions')}
        </th>
      </tr>
    </thead>
  )

  return (
    <div className={`${pageScope('tpsm-inventory')} ${listStyles.pageStack}`}>
      <GovernanceListingSection title={t('tpsm.inventory.metricsTitle', 'Supplier metrics')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.inventory.totalSuppliers', 'Total Suppliers')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{stats.total}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTilePurple}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.inventory.activeAssessments', 'Active Assessments')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textPurple}`}>{stats.activeAssessments}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.inventory.averageCompliance', 'Average Compliance')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>{stats.avgCompliance}%</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
            <p className={listStyles.metricTileLabel}>{t('tpsm.inventory.highRiskSuppliers', 'High Risk Suppliers')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{stats.highRisk}</p>
          </div>
        </div>
      </GovernanceListingSection>

      {criticalityData.length > 0 && (
        <div className={listStyles.sectionSpacing}>
          <SuppliersCriticalityDistributionChart
            data={criticalityData.map((item) => ({
              supplierId: item.supplierId,
              supplierName: item.supplierName,
              criticalityScore: item.criticalityScore || 0,
              criticalityLevel: item.criticalityLevel || 'NOT_ASSESSED',
            }))}
          />
        </div>
      )}

      <div className={listStyles.sectionWithExternalTitle}>
        <h2 className={listStyles.panelExternalTitle}>{t('tpsm.inventory.title', 'Supplier Inventory')}</h2>
        <GovernanceListingSection
          toolbar={
          <div className={listStyles.panelToolbarFull}>
            <div className={listStyles.filtersRow}>
              <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                <SearchField
                  label={t('tpsm.inventory.searchLabel', 'Search')}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={t('tpsm.inventory.searchPlaceholder', 'Search suppliers...')}
                  minWidth="14rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.inventory.filters.category', 'Category')}
                  options={categorySelectOptions}
                  value={filters.category}
                  onChange={(v) => setFilters({ ...filters, category: v ?? 'all' })}
                  minWidth="11rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.inventory.filters.criticality', 'Criticality')}
                  options={criticalitySelectOptions}
                  value={filters.criticality}
                  onChange={(v) => setFilters({ ...filters, criticality: v ?? 'all' })}
                  minWidth="11rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.inventory.filters.status', 'Status')}
                  options={statusSelectOptions}
                  value={filters.status}
                  onChange={(v) => setFilters({ ...filters, status: v ?? 'all' })}
                  minWidth="11rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  label={t('tpsm.inventory.filters.standards', 'Standards')}
                  options={standardsSelectOptions}
                  value={filters.standards}
                  onChange={(v) => setFilters({ ...filters, standards: v ?? 'all' })}
                  minWidth="11rem"
                />
              </div>
              {hasActiveFilters && (
                <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                  <Button
                    type="button"
                    size="small"
                    color="outline"
                    icon={<X className={listStyles.badgeIcon} aria-hidden />}
                    onClick={clearFilters}
                  >
                    {t('tpsm.inventory.clearFilters', 'Clear')}
                  </Button>
                </div>
              )}
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <span className={listStyles.fieldLabel}>{t('tpsm.inventory.viewMode', 'View')}</span>
                <div className={listStyles.viewToggle} role="group" aria-label={t('tpsm.inventory.viewMode', 'View')}>
                  <button
                    type="button"
                    className={`${listStyles.viewToggleBtn} ${viewMode === 'table' ? listStyles.viewToggleBtnActive : ''}`.trim()}
                    onClick={() => setViewMode('table')}
                    title={t('tpsm.inventory.viewTable', 'Table View')}
                    aria-pressed={viewMode === 'table'}
                  >
                    <List className={listStyles.badgeIcon} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className={`${listStyles.viewToggleBtn} ${viewMode === 'cards' ? listStyles.viewToggleBtnActive : ''}`.trim()}
                    onClick={() => setViewMode('cards')}
                    title={t('tpsm.inventory.viewCards', 'Card View')}
                    aria-pressed={viewMode === 'cards'}
                  >
                    <Grid className={listStyles.badgeIcon} aria-hidden />
                  </button>
                </div>
              </div>
              <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                <Button
                  type="button"
                  color="primary"
                  onClick={() => setShowAddModal(true)}
                  icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
                >
                  {t('tpsm.inventory.addSupplier', 'Add Supplier')}
                </Button>
              </div>
            </div>
          </div>
        }
        >
        {loading ? (
          <div className={listStyles.tableWrap}>
            <table className={listStyles.table}>
              {supplierInventoryThead}
              <tbody>
                <TableLoadingRow colSpan={7}>{t('common.loading', 'Loading...')}</TableLoadingRow>
              </tbody>
            </table>
          </div>
        ) : error ? (
          <div className={listStyles.tableWrap}>
            <table className={listStyles.table}>
              {supplierInventoryThead}
              <tbody>
                <TableErrorRow colSpan={7}>{error}</TableErrorRow>
              </tbody>
            </table>
          </div>
        ) : filteredSuppliers.length === 0 ? (
          viewMode === 'table' ? (
            <div className={listStyles.tableWrap}>
              <table className={listStyles.table}>
                {supplierInventoryThead}
                <tbody>
                  <TableEmptyRow colSpan={7}>
                    {t('tpsm.inventory.noSuppliers', 'No suppliers found')}
                  </TableEmptyRow>
                </tbody>
              </table>
            </div>
          ) : (
            <div className={listStyles.emptyState}>{t('tpsm.inventory.noSuppliers', 'No suppliers found')}</div>
          )
        ) : viewMode === 'table' ? (
          <div className={listStyles.tableWrap}>
            <table className={listStyles.table}>
              {supplierInventoryThead}
              <tbody>
                  {filteredSuppliers.map((s) => {
                    const isExpanded = expandedSupplier === s.id
                    const assessments = supplierAssessments[s.id] || []
                    const completedAssessments = assessments.filter((a: any) => a.status === 'COMPLETED')
                    const avgCompliance = completedAssessments.length > 0
                      ? Math.round(completedAssessments.reduce((sum: number, a: any) => sum + (a.complianceScore || 0), 0) / completedAssessments.length)
                      : s.compliance || 0
                    const activeStandards = new Set(assessments.map((a: any) => a.standardCode)).size
                    const totalStandards = s.standards?.length || 0
                    const lastUpdated = assessments.length > 0
                      ? assessments.sort((a: any, b: any) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())[0]?.updatedAt || assessments[0]?.createdAt
                      : s.lastAssessment
                    
                    return (
                      <React.Fragment key={s.id}>
                        <tr className={listStyles.row}>
                          <td className={`${listStyles.td} ${listStyles.cellStrong}`}>{s.name}</td>
                          <td className={listStyles.td}>{s.categoryAccess || '—'}</td>
                          <td className={listStyles.td}>
                            <span className={`${listStyles.badge} ${getCriticalityBadgeClass(s.criticality)}`}>
                              {s.criticality || '—'}
                            </span>
                          </td>
                          <td className={listStyles.td}>
                            <div className={listStyles.filterChips}>
                              {(s.standards || []).slice(0, 3).map((st: string) => (
                                <span key={st} className={`${listStyles.badge} ${listStyles.badgeInProgress}`}>
                                  {st}
                                </span>
                              ))}
                              {(s.standards || []).length > 3 && (
                                <span className={`${listStyles.badge} ${listStyles.badgeDefault}`}>
                                  +{(s.standards || []).length - 3}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={listStyles.td}>
                            <span className={`${listStyles.compliancePill} ${getCompliancePillClass(s.compliance || 0)}`}>
                              {s.compliance || 0}%
                            </span>
                          </td>
                          <td className={listStyles.td}>
                            {s.lastAssessment ? new Date(s.lastAssessment).toLocaleDateString() : '—'}
                          </td>
                          <td className={listStyles.td}>
                            <div className={listStyles.actionsStack}>
                              {s.portalToken?.portalUrl && (
                                <div className={`${listStyles.actionsGap} text-sm ${listStyles.textBlue}`}>
                                  <LinkIcon className={listStyles.badgeIcon} aria-hidden />
                                  <span className="truncate max-w-[200px]" title={s.portalToken.portalUrl}>
                                    {s.portalToken.portalUrl}
                                  </span>
                                  <Button
                                    type="button"
                                    size="small"
                                    color="outline"
                                    iconOnly
                                    icon={copiedLink === s.id ? <Check className={listStyles.badgeIcon} aria-hidden /> : <Copy className={listStyles.badgeIcon} aria-hidden />}
                                    className="ml-1 px-1 py-0.5 text-xs bg-blue-100 rounded hover:bg-blue-200"
                                    onClick={async () => {
                                      await navigator.clipboard.writeText(s.portalToken!.portalUrl)
                                      setCopiedLink(s.id)
                                      setTimeout(() => setCopiedLink(null), 3000)
                                    }}
                                    title={t('tpsm.inventory.copyPortalLink', 'Copy Portal Link')}
                                  />
                                </div>
                              )}
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  size="small"
                                  color="secondary"
                                  className="px-2 py-1 text-xs"
                                  icon={<Eye className={listStyles.badgeIcon} aria-hidden />}
                                  onClick={() => navigate(`/tpsm/supplier/${s.id}`)}
                                  title={t('tpsm.inventory.viewProfile', 'View Supplier Profile')}
                                >
                                  {t('tpsm.inventory.view', 'View')}
                                </Button>
                                <Button
                                  type="button"
                                  size="small"
                                  color="secondary"
                                  className="px-2 py-1 text-xs"
                                  icon={<Edit className={listStyles.badgeIcon} aria-hidden />}
                                  onClick={() => setEditingSupplier(s)}
                                  title={t('tpsm.inventory.editSupplier', 'Edit Supplier')}
                                >
                                  {t('tpsm.inventory.edit', 'Edit')}
                                </Button>
                                {!s.portalToken?.portalUrl && (
                                  <Button
                                    type="button"
                                    size="small"
                                    color="secondary"
                                    className="px-2 py-1 text-xs"
                                    icon={<LinkIcon className={listStyles.badgeIcon} aria-hidden />}
                                    onClick={() => generatePortalLink(s.id)}
                                    title={t('tpsm.inventory.generatePortalLink', 'Generate Portal Link')}
                                  >
                                    {t('tpsm.inventory.generateLink', 'Generate')}
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  size="small"
                                  color="danger"
                                  className="px-2 py-1 text-xs"
                                  icon={<Trash2 className={listStyles.badgeIcon} aria-hidden />}
                                  onClick={() => deleteSupplier(s.id, s.name)}
                                  title={t('tpsm.inventory.deleteSupplier', 'Delete Supplier')}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className={listStyles.row}>
                            <td colSpan={7} className={listStyles.nestedCell}>
                              {/* Expanded view can show additional details if needed in the future */}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={listStyles.supplierCardGrid}>
              {filteredSuppliers.map((s) => {
                const isExpanded = expandedSupplier === s.id
                const assessments = supplierAssessments[s.id] || []
                // Calculate avgCompliance using the same algorithm as SupplierComplianceSummaryModal:
                // Get unique standards with highest score for each standard, then average
                const standardsWithScores = new Map<string, number>()
                assessments.forEach((a: any) => {
                  if (a.complianceScore !== undefined && a.complianceScore !== null) {
                    const code = a.standardCode
                    const score = a.complianceScore || 0
                    // Keep the highest score for each standard
                    if (!standardsWithScores.has(code) || standardsWithScores.get(code)! < score) {
                      standardsWithScores.set(code, score)
                    }
                  }
                })
                const standardScores = Array.from(standardsWithScores.values())
                const avgCompliance = standardScores.length > 0
                  ? Math.round(standardScores.reduce((sum, score) => sum + score, 0) / standardScores.length)
                  : s.compliance || 0
                const activeStandards = new Set(assessments.map((a: any) => a.standardCode)).size
                const totalStandards = s.standards?.length || 0
                const lastUpdated = assessments.length > 0
                  ? assessments.sort((a: any, b: any) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())[0]?.updatedAt || assessments[0]?.createdAt
                  : s.lastAssessment
                
                // Get last assessment user info if available
                const lastAssessment = assessments.length > 0
                  ? assessments.sort((a: any, b: any) => new Date(b.updatedAt || b.createdAt).getTime() - new Date(a.updatedAt || a.createdAt).getTime())[0]
                  : null
                const lastAssessmentUser = lastAssessment?.assessedBy || lastAssessment?.submittedBy || lastAssessment?.createdBy || null
                
                return (
                <div key={s.id} className="ds-card p-4">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">{s.name}</h3>
                      <Button
                        type="button"
                        size="small"
                        color="outline"
                        iconOnly
                        icon={isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        className="p-1 hover:bg-gray-100 rounded"
                        onClick={() => handleExpandSupplier(s.id)}
                        title={isExpanded ? t('tpsm.inventory.collapse', 'Collapse') : t('tpsm.inventory.expand', 'Expand')}
                      />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">{t('tpsm.inventory.card.category', 'Category')}: </span>
                        <span className="font-medium">{s.categoryAccess || '-'}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('tpsm.inventory.card.overallCompliance', 'Overall Compliance')}: </span>
                        <span className={`${listStyles.compliancePill} ${getCompliancePillClass(avgCompliance)}`}>
                          {avgCompliance}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('tpsm.inventory.card.standards', 'Standards')}: </span>
                        <span className="font-semibold">
                          {(s.standards || []).length > 0 
                            ? (s.standards || []).map((st: string, idx: number) => (
                                <span key={st}>
                                  {st}{idx < (s.standards || []).length - 1 ? ', ' : ''}
                                </span>
                              ))
                            : '-'
                          }
                        </span>
                      </div>
                      {s.lastAssessment && (
                        <div>
                          <span className="text-gray-600">{t('tpsm.inventory.card.lastAssessment', 'Last Assessment')}: </span>
                          <span className="font-medium">{new Date(s.lastAssessment).toLocaleDateString()}</span>
                        </div>
                      )}
                      {lastAssessmentUser && (
                        <div>
                          <span className="text-gray-600">{t('tpsm.inventory.card.responsiblePerson', 'Responsible Person')}: </span>
                          <span className="font-medium">
                            {typeof lastAssessmentUser === 'object' 
                              ? `${lastAssessmentUser.name || lastAssessmentUser.fullName || ''} ${lastAssessmentUser.email ? `(${lastAssessmentUser.email})` : ''}`.trim()
                              : lastAssessmentUser
                            }
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {s.portalToken?.portalUrl && (
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <div className="text-xs text-gray-600 mb-1">{t('tpsm.inventory.portalLink', 'Portal Link')}:</div>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={s.portalToken.portalUrl}
                            className="flex-1 text-xs px-2 py-1 bg-white border rounded"
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                          />
                          <Button
                            type="button"
                            size="small"
                            color="secondary"
                            className="px-2 py-1 text-xs"
                            icon={
                              copiedLink === s.id ? (
                                <Check className="h-3 w-3" />
                              ) : (
                                <Copy className="h-3 w-3" />
                              )
                            }
                            onClick={async () => {
                              await navigator.clipboard.writeText(s.portalToken!.portalUrl)
                              setCopiedLink(s.id)
                              setTimeout(() => setCopiedLink(null), 3000)
                            }}
                            title={t('tpsm.inventory.copyPortalLink', 'Copy Portal Link')}
                          />
                          <a
                            href={s.portalToken.portalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary px-2 py-1 text-xs flex items-center gap-1"
                            title={t('tpsm.inventory.openPortalLink', 'Open Portal Link')}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t flex items-center gap-2">
                      <Button
                        type="button"
                        size="small"
                        color="secondary"
                        className="px-2 py-1 text-xs"
                        icon={<Eye className="h-3 w-3" />}
                        onClick={() => navigate(`/tpsm/supplier/${s.id}`)}
                        title={t('tpsm.inventory.viewProfile', 'View Supplier Profile')}
                      >
                        {t('tpsm.inventory.view', 'View')}
                      </Button>
                      <Button
                        type="button"
                        size="small"
                        color="secondary"
                        className="px-2 py-1 text-xs"
                        icon={<Edit className="h-3 w-3" />}
                        onClick={() => setEditingSupplier(s)}
                        title={t('tpsm.inventory.editSupplier', 'Edit Supplier')}
                      >
                        {t('tpsm.inventory.edit', 'Edit')}
                      </Button>
                      {!s.portalToken?.portalUrl && (
                        <Button
                          type="button"
                          size="small"
                          color="secondary"
                          fullWidth
                          className="flex-1 text-xs"
                          icon={<LinkIcon className="h-3 w-3" />}
                          onClick={() => generatePortalLink(s.id)}
                        >
                          {t('tpsm.inventory.generateLink', 'Generate Link')}
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="small"
                        color="danger"
                        className="px-2 py-1 text-xs"
                        icon={<Trash2 className="h-3 w-3" />}
                        onClick={() => deleteSupplier(s.id, s.name)}
                        title={t('tpsm.inventory.deleteSupplier', 'Delete Supplier')}
                      />
                    </div>
                </div>
              )})}
            </div>
          )}
      </GovernanceListingSection>
      </div>

      {/* Add Supplier Modal */}
      <AddSupplierModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          fetchSuppliers()
          setShowAddModal(false)
        }}
        token={token}
      />

      {/* Edit Supplier Modal */}
      {editingSupplier && (
        <EditSupplierModal
          isOpen={!!editingSupplier}
          onClose={() => setEditingSupplier(null)}
          onSuccess={() => {
            fetchSuppliers()
            setEditingSupplier(null)
          }}
          token={token}
          supplier={editingSupplier}
        />
      )}
    </div>
  )
}

export default SupplierInventory
